// @flow
import { castArray, fromPairs } from 'lodash';
import { b2cPagesService, constants } from '@clubmed/utils';

import { getSiteSection } from 'Tracking/utils';
import logger from 'Logger';
import { PageNotFound } from 'Errors/errors';
import { PAGE_TRACKING, PREBOOKING_FORM, SUBMISSION_STATUS } from 'Client/cms/toolkit/constants';
import { isSeller } from 'Utils/seller/sellerUtils';
import { PAGE_DYNAMIC } from 'Utils/request/requestDictionary';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { formatLinesForLineComponent } from 'Utils/lines/formatLinesForLineComponent';
import { STATUS_CODE_200, STATUS_CODE_404 } from 'Utils/status-code/statusCode.constants';

export default async ({ appContext, location }: $Shape<GetInitialPropsContext<>>) => {
  const pageQuery = `
  query {
    page: pageByUrl(pageUrl: "${location.pathname}") {
      pageId
      pageType
      pageRegion { name breadcrumbLabel }
      pageCountry { name breadcrumbLabel }
      url
      title
      description
      lines
      meta { key value }
      hrefLang { url locale }
      productRegionPageUrl
      productCountryPageUrl
    }
  }`;
  return graphQLClient
    .runQuery(pageQuery, appContext, { requestName: PAGE_DYNAMIC })
    .then(response => {
      const { page } = response;

      if (!page) {
        logger.error(new PageNotFound({ location }));

        return {
          statusCode: STATUS_CODE_404,
        };
      }

      const pageType = page.pageType || page.pageId || 'none';
      const pageContext: PageContext = {
        pageId: page.pageId,
        pageTitle: page.title || constants.WEBSITE_NAME,
        ...(page.pageRegion ? { pageRegion: page.pageRegion } : null),
        ...(page.pageCountry ? { pageCountry: page.pageCountry } : null),
        // $FlowFixMe
        ...(page.productRegionPageUrl ? { productRegionPageUrl: page.productRegionPageUrl } : null),
        ...(page.productCountryPageUrl
          ? { productCountryPageUrl: page.productCountryPageUrl }
          : null),
        hrefLang: page.hrefLang,
        pageDescription: page.description,
        pageMeta: fromPairs(
          castArray(page.meta)
            .map(meta => meta && [meta.key, meta.value])
            .filter(Boolean),
        ),
        pageType,
        // default status
        statusCode: STATUS_CODE_200,
        siteSection: getSiteSection({ pageType }),
      };

      let pageLines = page.lines || [];
      // Filter non-form lines out of CxO prebookingform pages (form, error, success)
      const isPrebookingformPage = pageContext.pageType === b2cPagesService.PAGE_TYPES.PREBOOKING;

      if (isPrebookingformPage && isSeller(appContext) && pageLines) {
        pageLines = pageLines.filter(line => {
          const lineName = Object.keys(line)[0];
          return [PREBOOKING_FORM, SUBMISSION_STATUS].includes(lineName);
        });
      }

      return pageLines
        ? formatLinesForLineComponent({
            lines: pageLines,
            appContext,
            pageContext,
            // $FlowFixMe
            location,
            linesToAppend: [PAGE_TRACKING],
          }).then(lines => {
            return {
              pageContext,
              page: {
                ...page,
                lines,
              },
            };
          })
        : { pageContext, page };
    })
    .catch(() => {
      logger.error(new PageNotFound({ location }));

      return {
        statusCode: STATUS_CODE_404,
      };
    });
};
